import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff, Globe } from "lucide-react";
import { FcGoogle } from "react-icons/fc";
import "./App.css";
import { auth, googleProvider } from "./lib/firebase";
import { signInWithPopup } from "firebase/auth";
import { signup, signin, exchangeFirebaseIdToken, persistSession } from "./lib/authApi";
import { useAuth } from "./context/AuthContext";
import Toast from "./components/Toast";

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login: updateAuthContext } = useAuth();
  
  // Set initial tab based on URL
  const initialTab = location.pathname === "/signup" ? "signup" : "signin";
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Update tab when URL changes
  useEffect(() => {
    if (location.pathname === "/signup") {
      setActiveTab("signup");
    } else if (location.pathname === "/login") {
      setActiveTab("signin");
    }
  }, [location.pathname]);
  
  // Sign In form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  
  // Sign Up form state
  const [firstName, setFirst] = useState("");
  const [lastName, setLast] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [showSignupPassword, setShowSignupPassword] = useState(false);

  // Toast state for error notifications
  const [toast, setToast] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const showToast = (message, type = 'error') => {
    setToast({ message, type });
  };

  const closeToast = () => {
    setToast(null);
  };

  // Helper function to get user-friendly error messages
  const getErrorMessage = (error) => {
    if (!error) return "An error occurred";
    
    // Firebase Auth error codes
    const errorCode = error?.code || "";
    
    switch (errorCode) {
      case "auth/invalid-email":
        return "Please enter a valid email address";
      case "auth/user-disabled":
        return "This account has been disabled";
      case "auth/user-not-found":
        return "We couldn't find an account with this email";
      case "auth/wrong-password":
        return "Incorrect password. Please try again";
      case "auth/invalid-credential":
        return "Invalid email or password. Please check and try again";
      case "auth/email-already-in-use":
        return "This email is already registered. Try signing in instead";
      case "auth/weak-password":
        return "Please use a stronger password (at least 6 characters)";
      case "auth/network-request-failed":
        return "Connection error. Please check your internet and try again";
      case "auth/too-many-requests":
        return "Too many attempts. Please wait a few minutes and try again";
      case "auth/popup-closed-by-user":
        return "Sign-in cancelled";
      case "auth/cancelled-popup-request":
        return "Sign-in cancelled";
      case "auth/operation-not-allowed":
        return "This sign-in method is not enabled. Please contact support";
      default:
        // Clean up technical Firebase error messages
        const message = error?.message || "Authentication failed";
        // Remove "Firebase: " prefix and technical details
        const cleanMessage = message
          .replace(/^Firebase:\s*/i, '')
          .replace(/\s*\(auth\/[^)]+\)\.?$/i, '')
          .trim();
        
        // If the message is still too technical, provide a friendly fallback
        if (cleanMessage.length > 100 || cleanMessage.includes('Error')) {
          return "Something went wrong. Please try again";
        }
        
        return cleanMessage || "Authentication failed. Please try again";
    }
  };

  // Sign In Handler - Calls backend API
  async function handleSignIn(e) {
    e.preventDefault();
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      console.log("🔵 Calling backend signin API...");
      const session = await signin(email, password);
      console.log("🔵 Backend response received:", session);
      
      persistSession(session);
      console.log("🔵 Session persisted");
      
      // Update AuthContext so ProtectedRoute knows user is authenticated
      updateAuthContext(session.accessToken, session.user.id);
      console.log("🔵 AuthContext updated, navigating to dashboard...");
      
      navigate("/");
    } catch (err) {
      console.error("Login error:", err);
      const errorMsg = err?.response?.data?.message || err?.message || "Login failed";
      showToast(errorMsg, 'error');
    } finally {
      setIsLoading(false);
    }
  }

  // Sign Up Handler - Calls backend API
  async function handleSignUp(e) {
    e.preventDefault();
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      console.log("🟢 Calling backend signup API...");
      const session = await signup(signupEmail, signupPassword, firstName, lastName);
      console.log("🟢 Backend response received:", session);
      
      persistSession(session);
      console.log("🟢 Session persisted");
      
      // Update AuthContext so ProtectedRoute knows user is authenticated
      updateAuthContext(session.accessToken, session.user.id);
      console.log("🟢 AuthContext updated, navigating to dashboard...");
      
      navigate("/");
    } catch (err) {
      console.error("Signup error:", err);
      const errorMsg = err?.response?.data?.message || err?.message || "Signup failed";
      showToast(errorMsg, 'error');
    } finally {
      setIsLoading(false);
    }
  }

  // Google Sign In Handler
  async function handleGoogleAuth() {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      console.log("🔵 Starting Google authentication...");
      const cred = await signInWithPopup(auth, googleProvider);
      console.log("🔵 Google auth successful:", cred.user.uid);
      
      const idToken = await cred.user.getIdToken(true);
      console.log("🔵 Calling backend...");
      
      const session = await exchangeFirebaseIdToken(idToken);
      console.log("🔵 Backend response received:", session);
      
      persistSession(session);
      console.log("🔵 Session persisted");
      
      // Update AuthContext so ProtectedRoute knows user is authenticated
      updateAuthContext(session.accessToken, session.user.id);
      console.log("🔵 AuthContext updated, navigating to dashboard...");
      
      navigate("/");
    } catch (err) {
      console.error("Google auth error:", err);
      showToast(getErrorMessage(err), 'error');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="auth-shell">
      {/* Left Side - Brand */}
      <div className="auth-brand-side">
        <div className="auth-brand-content">
          <img 
            src="/Lateral Original.png" 
            alt="Sango" 
            className="auth-logo-large" 
          />
          <h1 className="auth-brand-title">
            Welcome to<br />Sango online
          </h1>
          <p className="auth-brand-subtitle">
            More than just investing
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="auth-form-side">
        <motion.div 
          className="auth-form-container"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          {/* Tab Switcher */}
          <div className="auth-tabs">
            <div className={`auth-tab-pill ${activeTab === "signup" ? "signup" : ""}`} />
            <button
              className={`auth-tab ${activeTab === "signin" ? "active" : ""}`}
              onClick={() => {
                setActiveTab("signin");
                navigate("/login", { replace: true });
              }}
            >
              Sign in
            </button>
            <button
              className={`auth-tab ${activeTab === "signup" ? "active" : ""}`}
              onClick={() => {
                setActiveTab("signup");
                navigate("/signup", { replace: true });
              }}
            >
              Register
            </button>
          </div>

          <div className="auth-form-content-wrapper">
            {/* Sign In Form */}
            {activeTab === "signin" && (
              <motion.div
                key="signin"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
              >
              <div className="auth-header">
                <h2 className="auth-title">Sign in</h2>
                <p className="auth-subtitle">
                  Sign in to continue. Remember, your password is yours, do not share it with anyone.
                </p>
              </div>

              <form onSubmit={handleSignIn} className="auth-form">
                <div className="auth-input-wrap">
                  <label className="auth-label">Email address or mobile number</label>
                  <input 
                    type="email"
                    className="auth-input"
                    value={email} 
                    onChange={(e) => setEmail(e.target.value)} 
                    placeholder="you@example.com"
                    required 
                  />
                </div>

                <div className="auth-input-wrap">
                  <label className="auth-label">Enter password</label>
                  <div className="auth-password-wrapper">
                    <input 
                      type={showPassword ? "text" : "password"}
                      className="auth-input"
                      value={password} 
                      onChange={(e) => setPassword(e.target.value)} 
                      placeholder="Enter password"
                      required 
                      style={{ paddingRight: '40px' }}
                    />
                    <button
                      type="button"
                      className="auth-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div className="auth-forgot-password">
                  <a href="#forgot">Forgot your password?</a>
                </div>

                <motion.button 
                  whileTap={{ scale: 0.98 }}
                  className="auth-submit auth-submit-primary"
                  type="submit"
                  disabled={isLoading}
                  style={{ opacity: isLoading ? 0.7 : 1 }}
                >
                  {isLoading ? "Signing in..." : "Sign in"}
                </motion.button>

                <div className="auth-divider">OR</div>

                <button 
                  type="button" 
                  className="auth-submit"
                  onClick={handleGoogleAuth}
                  disabled={isLoading}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    gap: '8px',
                    opacity: isLoading ? 0.7 : 1
                  }}
                >
                  <FcGoogle size={20} />
                  {isLoading ? "Please wait..." : "Continue with Google"}
                </button>
              </form>
              </motion.div>
            )}

            {/* Sign Up Form */}
            {activeTab === "signup" && (
            <motion.div
              key="signup"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="auth-header">
                <h2 className="auth-title">Create your account</h2>
                <p className="auth-subtitle">
                  Invest in forests and carbon credits across Africa. Join our community of sustainable investors today.
                </p>
              </div>

              <form onSubmit={handleSignUp} className="auth-form">
                <div className="grid-2">
                  <div className="auth-input-wrap">
                    <label className="auth-label">First name</label>
                    <input 
                      type="text"
                      className="auth-input"
                      value={firstName} 
                      onChange={(e) => setFirst(e.target.value)} 
                      placeholder="First name"
                      required 
                    />
                  </div>
                  <div className="auth-input-wrap">
                    <label className="auth-label">Last name</label>
                    <input 
                      type="text"
                      className="auth-input"
                      value={lastName} 
                      onChange={(e) => setLast(e.target.value)} 
                      placeholder="Last name"
                      required 
                    />
                  </div>
                </div>

                <div className="auth-input-wrap">
                  <label className="auth-label">Email address</label>
                  <input 
                    type="email"
                    className="auth-input"
                    value={signupEmail} 
                    onChange={(e) => setSignupEmail(e.target.value)} 
                    placeholder="you@example.com"
                    required 
                  />
                </div>

                <div className="auth-input-wrap">
                  <label className="auth-label">Password</label>
                  <div className="auth-password-wrapper">
                    <input 
                      type={showSignupPassword ? "text" : "password"}
                      className="auth-input"
                      value={signupPassword} 
                      onChange={(e) => setSignupPassword(e.target.value)} 
                      placeholder="At least 8 characters"
                      required 
                      style={{ paddingRight: '40px' }}
                    />
                    <button
                      type="button"
                      className="auth-password-toggle"
                      onClick={() => setShowSignupPassword(!showSignupPassword)}
                    >
                      {showSignupPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <motion.button 
                  whileTap={{ scale: 0.98 }}
                  className="auth-submit auth-submit-primary"
                  type="submit"
                  disabled={isLoading}
                  style={{ opacity: isLoading ? 0.7 : 1 }}
                >
                  {isLoading ? "Creating account..." : "Create account"}
                </motion.button>

                <div className="auth-divider">OR</div>

                <button 
                  type="button" 
                  className="auth-submit"
                  onClick={handleGoogleAuth}
                  disabled={isLoading}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    gap: '8px',
                    opacity: isLoading ? 0.7 : 1
                  }}
                >
                  <FcGoogle size={20} />
                  {isLoading ? "Please wait..." : "Sign up with Google"}
                </button>
              </form>
              </motion.div>
            )}
          </div>
        </motion.div>

        <div className="auth-language-selector">
          <button className="language-btn">
            <Globe size={16} />
            English
          </button>
        </div>
      </div>

      {/* Toast notification for errors */}
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={closeToast}
          duration={4000}
        />
      )}
    </div>
  );
}

